import {
  AlertCircle,
  ExternalLink,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  Wand2,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { type ProbeState } from '../hooks/useSafeProbe.js'
import { DEMO_SAFE } from '../lib/constants.js'
import { short } from '../decode.js'

interface Props {
  /** Address the user passed via `?safe=`. */
  inputAddress: string
  /** Guardian EOA address from /v1/health, if reachable. */
  guardianAddress: string | null
  /** Probe state owned by App.tsx (so we don't double-fetch). When the
   *  probe finds SafeWard, App swaps to the dashboard branch and this
   *  card unmounts before we ever render the safeward-main flash. */
  probe: ProbeState
}

export function OnboardingCard({
  inputAddress,
  guardianAddress,
  probe,
}: Props) {
  return (
    <Card className="mb-4 border-l-4 border-l-amber-500">
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="size-5 text-amber-500" />
          This Safe isn't protected by SafeWard yet
        </CardTitle>
        <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-400">
          onboarding
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <ProbeSummary inputAddress={inputAddress} probe={probe} />

        {probe.status === 'done' &&
          (probe.result.kind === 'safe-without-safeward' ||
            probe.result.kind === 'unreachable') && (
            <EnableSteps
              inputAddress={inputAddress}
              guardianAddress={guardianAddress}
            />
          )}
      </CardContent>
      <CardFooter className="flex flex-wrap items-center gap-2">
        <Button asChild variant="outline">
          <a
            href={`https://app.safe.global/home?safe=eth:${inputAddress}`}
            target="_blank"
            rel="noopener"
          >
            <ExternalLink />
            Open in Safe&#123;Wallet&#125;
          </a>
        </Button>
        <Button asChild variant="ghost">
          <a href={`?safe=${DEMO_SAFE}`}>
            <Wand2 />
            Try the demo Safe
          </a>
        </Button>
        <Button asChild variant="ghost" className="ml-auto">
          <a href="?">← back to landing</a>
        </Button>
      </CardFooter>
    </Card>
  )
}

function ProbeSummary({
  inputAddress,
  probe,
}: {
  inputAddress: string
  probe: ProbeState
}) {
  if (probe.status === 'idle' || probe.status === 'loading') {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Checking <code className="font-mono">{short(inputAddress)}</code> on
        Ethereum mainnet…
      </div>
    )
  }

  const r = probe.result

  if (r.kind === 'not-a-safe') {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
          <AlertCircle className="size-4" />
          Not a Safe contract
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          We pinged{' '}
          <code className="font-mono text-foreground">{inputAddress}</code> on
          mainnet but it doesn't look like a Safe (
          <span className="italic">{r.reason}</span>). Did you paste the right
          address? SafeWard only knows Ethereum mainnet for now.
        </p>
      </div>
    )
  }

  if (r.kind === 'unreachable') {
    return (
      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-amber-400">
          <AlertCircle className="size-4" />
          Couldn't reach Ethereum mainnet
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Public RPC error: <span className="italic">{r.error}</span>. We can
          still walk you through enabling SafeWard manually below.
        </p>
      </div>
    )
  }

  if (r.kind === 'safeward-main') {
    // Auto-redirect lives in the parent useEffect; this is the brief flash
    // the visitor sees before reload.
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-400">
        <ShieldCheck className="size-4" />
        SafeWard is enabled on this Safe — opening dashboard…
      </div>
    )
  }

  // safe-without-safeward
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        We confirmed{' '}
        <code className="font-mono text-foreground">{inputAddress}</code> is a
        Safe{r.version ? ` (v${r.version})` : ''} on Ethereum mainnet, but
        SafeWard isn't one of its enabled modules.
      </p>
      <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs">
        <div className="text-muted-foreground">
          {r.modules.length === 0
            ? 'No modules enabled on this Safe yet.'
            : `${r.modules.length} module${
                r.modules.length === 1 ? '' : 's'
              } currently enabled:`}
        </div>
        {r.modules.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {r.modules.map((m) => (
              <li key={m} className="font-mono text-foreground">
                {short(m)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function EnableSteps({
  inputAddress,
  guardianAddress,
}: {
  inputAddress: string
  guardianAddress: string | null
}) {
  const txBuilderUrl = `https://app.safe.global/apps/open?safe=eth:${inputAddress}&appUrl=${encodeURIComponent(
    'https://apps-portal.safe.global/tx-builder',
  )}`
  const newSafeUrl = 'https://app.safe.global/new-safe/create'
  const guardian =
    guardianAddress ?? '<Guardian address — refresh once SafeWard is reachable>'

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        SafeWard routes dApp transactions through a co-signer "Module Safe"
        that you and the Guardian both have to approve before anything
        executes. Add it in two steps.
      </p>

      <Step
        n={1}
        title="Deploy a Module Safe (3 owners, 2-of-3 threshold)"
        body={
          <>
            Open Safe{'{'}Wallet{'}'} and create a new Safe with three owners:
            <ol className="mt-1.5 ml-4 list-decimal space-y-1 text-sm">
              <li>
                <strong>Daily signer</strong> — the EOA you pair via
                WalletConnect. Bound to SafeWard's smart-permit policy.
              </li>
              <li>
                <strong>SafeWard Guardian</strong> — co-signs dApp txs after
                running policy:
                <br />
                <code className="mt-1 inline-block break-all rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                  {guardian}
                </code>
              </li>
              <li>
                <strong>Root signer</strong> — a cold key you control. Lets
                you rotate the Guardian (daily + root) if it's ever
                compromised or you want to swap providers.
              </li>
            </ol>
            <p className="mt-2">
              Set the threshold to <strong>2-of-3</strong>: daily + Guardian
              for normal dApp txs, daily + root for Guardian rotation. Save
              the new Safe's address — that's your Module Safe.
            </p>
          </>
        }
        action={
          <Button asChild variant="outline" size="sm">
            <a href={newSafeUrl} target="_blank" rel="noopener">
              <ExternalLink />
              New Safe in Safe&#123;Wallet&#125;
            </a>
          </Button>
        }
      />

      <Step
        n={2}
        title="Enable the Module Safe as a Module on this Safe"
        body={
          <>
            Open the Transaction Builder on{' '}
            <code className="font-mono text-foreground">{inputAddress}</code>,
            paste the Safe address itself as the contract, and tick{' '}
            <strong>Use Implementation ABI</strong> — the proxy ABI doesn't
            expose <code className="font-mono text-foreground">enableModule</code>{' '}
            so without that toggle the dropdown won't list it. Then call{' '}
            <code className="font-mono text-foreground">enableModule</code>{' '}
            with the Module Safe address from step 1. After it executes,
            dApps paired here will be co-signed by the Guardian.
          </>
        }
        action={
          <Button asChild variant="outline" size="sm">
            <a href={txBuilderUrl} target="_blank" rel="noopener">
              <ExternalLink />
              Open Transaction Builder
            </a>
          </Button>
        }
      />

      <Step
        n={3}
        title="Reload SafeWard with your Module Safe"
        body={
          <>
            Once enabled, point SafeWard at the new Module Safe address by
            opening{' '}
            <code className="font-mono text-foreground">
              ?safe=&lt;module-safe&gt;
            </code>{' '}
            (you can also paste the Main Safe address — SafeWard will resolve
            to the Module Safe automatically).
          </>
        }
      />
    </div>
  )
}

function Step({
  n,
  title,
  body,
  action,
}: {
  n: number
  title: string
  body: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-xs text-muted-foreground">
          step {n}
        </span>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <p className="mt-1.5 text-sm text-muted-foreground">{body}</p>
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}
